import * as React from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { useShallow } from 'zustand/shallow'
import { useLocalize } from '../context/localize/LocalizeContext'
import { DiffView } from './DiffView'

interface SubNavigationItemProps {
  to: string
  children?: React.ReactNode
  matchSubRoutes?: boolean
  isActive?: (pathname: string, to: string) => boolean
  disabled?: boolean
}

export const SubNavigationItem = ({
  to,
  children,
  matchSubRoutes = false,
  isActive: customIsActive,
  disabled = false,
}: SubNavigationItemProps) => {
  const location = useLocation()
  const wrapConfirm = useWrapConfirm()
  const { isDirty, setDirty, diffEntries } = useDirtyStore(
    useShallow((s) => ({ isDirty: s.isDirty, setDirty: s.setDirty, diffEntries: s.diffEntries })),
  )
  const navigateFn = useNavigate()
  const { t } = useLocalize()

  const isActive = customIsActive
    ? customIsActive(location.pathname, to)
    : !matchSubRoutes
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`)

  const navigate = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      // Flush synchronously so the global dirty store is false before navigateFn
      // triggers the router, preventing DirtyGuard's useBlocker from double-firing.
      flushSync(() => setDirty(false))
      navigateFn(to)
    },
    [navigateFn, to, setDirty],
  )

  const navigateWrapped = React.useMemo(() => {
    if (!isDirty) return navigate

    const hasDiff = diffEntries.length > 0
    return wrapConfirm(navigate, {
      title: t('configuration.unsaved-changes-title'),
      message: t('configuration.unsaved-changes-confirm'),
      content: hasDiff ? <DiffView entries={diffEntries} /> : undefined,
      confirmText: t('configuration.unsaved-changes-leave'),
      cancelText: t('configuration.unsaved-changes-stay'),
      classNamePopupContent: hasDiff ? 'tw-w-[700px]' : 'tw-w-[500px]',
    })
  }, [isDirty, navigate, wrapConfirm, t, diffEntries])

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    // allow open in new tab / middle click
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.button === 1
    ) {
      return
    }

    navigateWrapped(event)
  }

  if (disabled) {
    return (
      <span className="tw-z-10 tw-cursor-not-allowed tw-text-nowrap tw-border-b-[2px] tw-border-transparent tw-p-4 tw-text-sm tw-font-medium tw-text-gray-300">
        {children}
      </span>
    )
  }

  return (
    <span
      onClick={handleClick}
      className={`tw-z-10 tw-text-nowrap tw-p-4 tw-text-sm tw-font-medium hover:tw-no-underline ${
        isActive
          ? 'tw-z-10 tw-border-b-[2px] tw-border-blue-600 tw-font-medium tw-text-blue-600'
          : 'tw-border-b-[2px] tw-border-transparent tw-text-content-base hover:tw-text-blue-600'
      }`}
    >
      {children}
    </span>
  )
}
